import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  ArrowRight,
  CheckCircle2,
  Wrench,
  Hammer,
  Paintbrush,
  Droplets,
  Zap,
  Home as HomeIcon,
  ChevronRight,
  MapPin,
  Shield,
  Clock,
  ExternalLink
} from 'lucide-react';
import { companyInfo, homeownerServices } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ HERO SECTION ============
const HeroSection = () => (
  <section className="relative pt-10 pb-8 sm:pt-20 sm:pb-14 lg:pt-24 lg:pb-20 bg-gradient-to-b from-brand-navy via-slate-900 to-brand-navy/95 overflow-hidden">
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
    </div>
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <HomeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-brand-yellow" />
          <span className="text-brand-yellow font-medium text-sm sm:text-base">For Homeowners</span>
        </div>
        
        <h1 className="text-[28px] leading-[1.15] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white sm:leading-tight mb-3 sm:mb-6">
          Property Maintenance for <span className="text-brand-yellow">Homeowners</span>
        </h1>
        
        <p className="text-[16px] leading-relaxed sm:text-lg lg:text-xl text-slate-300 mb-5 sm:mb-8">
          Reliable repairs, maintenance, decorating, carpentry and property improvements from one multi-trade property maintenance company.
        </p>
        
        <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-4 mb-5 sm:mb-8">
          <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold text-[16px] sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-auto" asChild>
            <a href="#contact">
              Request a Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy font-semibold text-[16px] sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-auto" asChild>
            <a href={`tel:${companyInfo.phone}`}>
              <Phone className="w-5 h-5 mr-2" />
              {companyInfo.phone}
            </a>
          </Button>
        </div>
        
        {/* Trust points - compact on mobile */}
        <div className="grid grid-cols-1 gap-1.5 sm:flex sm:flex-wrap sm:gap-4 text-[15px] sm:text-base">
          <div className="flex items-center gap-2 bg-white/8 sm:bg-transparent px-2.5 py-1.5 sm:p-0 rounded">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-brand-yellow" />
            <span className="text-white sm:text-slate-300">£5m Insurance</span>
          </div>
          <div className="flex items-center gap-2 bg-white/8 sm:bg-transparent px-2.5 py-1.5 sm:p-0 rounded">
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-brand-yellow" />
            <span className="text-white sm:text-slate-300">12-Month Guarantee</span>
          </div>
          <div className="flex items-center gap-2 bg-white/8 sm:bg-transparent px-2.5 py-1.5 sm:p-0 rounded">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-brand-yellow" />
            <span className="text-white sm:text-slate-300">Greater Manchester & Cheshire</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============ SERVICES SECTION ============
const ServicesSection = () => (
  <section className="py-12 sm:py-16 lg:py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10 sm:mb-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-brand-navy mb-3">
          Homeowner Services
        </h2>
        <p className="text-brand-slate max-w-2xl mx-auto">
          From small repairs to larger property improvements, we handle the work so you do not have to coordinate multiple tradespeople.
        </p>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {homeownerServices.map((service) => (
          <Card key={service.title} className="border-slate-200 hover:border-brand-yellow hover:shadow-lg transition-all bg-white">
            <CardContent className="p-5 sm:p-6">
              <h3 className="text-lg font-bold text-brand-navy mb-2">{service.title}</h3>
              <p className="text-brand-slate text-sm">{service.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// ============ ONE CONTACT SECTION ============
const OneContactSection = () => (
  <section className="py-12 sm:py-16 bg-brand-grey">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-navy mb-4">
            One Contact for Multiple Jobs
          </h2>
          <p className="text-brand-slate mb-6 leading-relaxed">
            Most homes need a mixture of jobs at once — perhaps some carpentry, decorating, a plaster repair and a few smaller jobs. Instead of finding and coordinating several different tradespeople, you deal with us once and we coordinate the work. No chasing different tradespeople.
          </p>
          <ul className="space-y-3 mb-6">
            {[
              'Tell us what you need - we handle the rest',
              'Multiple trades under one company',
              'No need to manage different contractors',
              'Consistent quality across all work',
              'Single invoice for combined work'
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                <span className="text-brand-slate">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-brand-slate text-sm">
            Whether it is a single repair or a whole-house refresh, we make property maintenance straightforward.
          </p>
        </div>
        
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200">
          <h3 className="text-xl font-bold text-brand-navy mb-4">Example Multi-Trade Job</h3>
          <p className="text-brand-slate text-sm mb-4">A typical property refresh might include:</p>
          <ul className="space-y-2 mb-6">
            {[
              'Plaster repairs to walls and ceilings',
              'Full house redecoration',
              'New internal doors and handles',
              'Skirting and architrave renewal',
              'Minor plumbing fixes',
              'General making-good throughout'
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 text-brand-slate text-sm">
                <div className="w-1.5 h-1.5 bg-brand-yellow rounded-full flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-brand-slate text-sm italic">
            One quote. One contractor. One point of contact.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ============ RELATED SERVICES SECTION ============
const RelatedServicesSection = () => {
  const relatedServices = [
    { name: 'Handyman Services', href: '/handyman-services', icon: Wrench, desc: 'General repairs and odd jobs' },
    { name: 'Painting & Decorating', href: '/painting-decorating', icon: Paintbrush, desc: 'Interior and exterior painting' },
    { name: 'Carpentry & Joinery', href: '/carpentry-joinery', icon: Hammer, desc: 'Doors, skirting, woodwork' },
    { name: 'Plastering & Repairs', href: '/plastering-repairs', icon: Droplets, desc: 'Plaster repairs and finishing' },
    { name: 'Property Refurbishment', href: '/property-refurbishment', icon: HomeIcon, desc: 'Larger multi-trade projects' }
  ];

  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-brand-navy mb-3 text-center">
          Specialist Services
        </h2>
        <p className="text-brand-slate text-center mb-8 sm:mb-10 max-w-2xl mx-auto">
          Explore our dedicated service pages for more details on specific trades.
        </p>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {relatedServices.map((service) => (
            <Link
              key={service.name}
              to={service.href}
              className="flex items-center gap-4 p-4 bg-brand-grey rounded-xl hover:bg-slate-100 hover:border-brand-yellow border border-transparent transition-all group"
            >
              <div className="w-12 h-12 bg-brand-navy rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-brand-yellow transition-colors">
                <service.icon className="w-6 h-6 text-brand-yellow group-hover:text-brand-navy transition-colors" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-brand-navy group-hover:text-brand-yellow transition-colors">{service.name}</h3>
                <p className="text-sm text-brand-slate truncate">{service.desc}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-brand-slate ml-auto flex-shrink-0" />
            </Link>
          ))}
        </div>
        
        <div className="mt-8 p-4 bg-brand-grey rounded-xl text-center">
          <p className="text-brand-slate text-sm mb-2">Our specialist trade websites:</p>
          <div className="flex flex-wrap justify-center gap-4">
            {companyInfo.websites.slice(0, 4).map((site) => (
              <a
                key={site.name}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-brand-navy hover:text-brand-yellow font-medium text-sm"
              >
                {site.name} <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ AREAS SECTION ============
const AreasSection = () => {
  const areas = [
    'Manchester', 'Stockport', 'Cheadle', 'Didsbury', 'Sale', 'Altrincham',
    'Salford', 'Trafford', 'Oldham', 'Warrington', 'Wilmslow', 'Macclesfield'
  ];

  return (
    <section className="py-12 sm:py-16 bg-brand-grey">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-navy mb-3">
            Areas We Cover
          </h2>
          <p className="text-brand-slate">
            Greater Manchester, Cheshire and surrounding areas
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
          {areas.map((area) => (
            <span
              key={area}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white text-brand-slate rounded-full text-sm font-medium border border-slate-200"
            >
              {area}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ CONTACT SECTION ============
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', postcode: '', job_description: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: formData.job_description,
          trade_type: 'general',
          customer_type: 'homeowner',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/homeowner-services'
        }),
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', postcode: '', job_description: '', honeypot: '' });
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-white scroll-mt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-navy mb-3">
            Get a Quote
          </h2>
          <p className="text-brand-slate">
            Tell us what you need and we will get back to you, usually within 24 hours.
          </p>
        </div>
        
        <Card className="border-slate-200">
          <CardContent className="p-6 sm:p-8">
            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">Thank You!</h3>
                <p className="text-brand-slate">We have received your enquiry and will be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" name="honeypot" value={formData.honeypot} onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })} className="hidden" tabIndex="-1" autoComplete="off" />
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                    <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full name" className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                    <Input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="you@example.com" className="h-12" />
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone</label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="07xxx xxxxxx" className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode / Area</label>
                    <Input value={formData.postcode} onChange={(e) => setFormData({ ...formData, postcode: e.target.value })} placeholder="e.g., M1 or Stockport" className="h-12" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-brand-slate mb-1.5">What do you need help with? *</label>
                  <Textarea required value={formData.job_description} onChange={(e) => setFormData({ ...formData, job_description: e.target.value })} placeholder="Describe the work you need..." rows={4} />
                </div>
                
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}
                
                <Button type="submit" size="lg" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 sm:h-14" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Request a Quote'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <p className="text-center text-brand-slate text-sm">
                  Or call us: <a href={`tel:${companyInfo.phone}`} className="text-brand-navy font-semibold hover:text-brand-yellow">{companyInfo.phone}</a>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// ============ FOOTER ============
const Footer = () => (
  <footer className="bg-brand-navy py-8 sm:py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <p className="text-xl font-bold text-white mb-2">Expert Property Maintenance</p>
        <p className="text-slate-400 mb-4">Property maintenance for landlords, letting agents and homeowners</p>
        <div className="flex justify-center gap-6 text-slate-400 text-sm">
          <Link to="/landlord-services" className="hover:text-white">Landlord Services</Link>
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/join" className="hover:text-white">Join Our Team</Link>
        </div>
        <p className="text-slate-500 text-sm mt-6">© {new Date().getFullYear()} Expert Property Maintenance</p>
      </div>
    </div>
  </footer>
);

// ============ MAIN COMPONENT ============
const HomeownerServices = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Property Maintenance for Homeowners | Greater Manchester & Cheshire | Expert Property Maintenance</title>
        <meta name="description" content="Reliable repairs, maintenance, decorating, carpentry and property improvements for homeowners across Greater Manchester & Cheshire. One multi-trade company for all your property needs." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/homeowner-services" />
        <meta property="og:title" content="Property Maintenance for Homeowners | Expert Property Maintenance" />
        <meta property="og:description" content="Reliable repairs, maintenance, decorating, carpentry and property improvements from one multi-trade property maintenance company." />
        <meta property="og:url" content="https://expert-propertymaintenance.co.uk/homeowner-services" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header variant="default" companyPhone={companyInfo.phone} />
      <main>
        <HeroSection />
        <ServicesSection />
        <OneContactSection />
        <RelatedServicesSection />
        <AreasSection />
        <ContactSection />
      </main>
      <Footer />
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default HomeownerServices;
